import React, { useState } from "react";
import { motion } from "framer-motion";
import { LuEye, LuEyeClosed } from "react-icons/lu";
import { containerVariants } from "../signup/motion";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const MIN_PASSWORD_LENGTH = 8;
const MAX_PASSWORD_LENGTH = 25;

const Step3 = ({ setStep,tempToken }) => {
  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState({
    newPassword: false,
    confirmPassword: false,
  });

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.newPassword || !formData.confirmPassword) {
      setError("Please enter all fields");
      return;
    }

    if (
      formData.newPassword.length < MIN_PASSWORD_LENGTH ||
      formData.newPassword.length > MAX_PASSWORD_LENGTH
    ) {
      setError(`Password must be between ${MIN_PASSWORD_LENGTH} and ${MAX_PASSWORD_LENGTH} characters`);
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/user/resetPassword`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tempToken}`,
        },
        body: JSON.stringify({ newPassword: formData.newPassword }),
      });
      const data = await response.json();

      if (!response.ok) {
      
        throw new Error(data.message || "Failed to reset password");
      }
      setStep(4)
      // console.log("Password reset successfully");
    } catch (err) {
      setError(err.message || "An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      key="new-password"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="bg-white py-8 px-10 rounded-sm shadow-custom border border-[#2A3D66]/40 w-full max-w-xl"
    >
      <h2 className="text-2xl mb-6">Reset Password</h2>
      <form onSubmit={handlePasswordSubmit}>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-gray-700 mb-1">Enter new password</label>
            <div className="relative">
              <input
                type={showPassword.newPassword ? "text" : "password"}
                value={formData.newPassword}
                onChange={(e) =>
                  setFormData({ ...formData, newPassword: e.target.value.slice(0, MAX_PASSWORD_LENGTH) })
                }
                className="w-full px-2.5 py-1.5 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 border border-gray-300 rounded-md bg-gray-50 [&::-ms-reveal]:hidden [&::-ms-clear]:hidden"
                minLength={MIN_PASSWORD_LENGTH}
                maxLength={MAX_PASSWORD_LENGTH}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => ({ ...prev, newPassword: !prev.newPassword }))}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-800"
                aria-label={showPassword.newPassword ? "Hide password" : "Show password"}
              >
                {showPassword.newPassword ? <LuEye className="w-4 h-4" /> : <LuEyeClosed className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">
              Confirm new password
            </label>
            <div className="relative">
              <input
                type={showPassword.confirmPassword ? "text" : "password"}
                value={formData.confirmPassword}
                onChange={(e) =>
                  setFormData({ ...formData, confirmPassword: e.target.value.slice(0, MAX_PASSWORD_LENGTH) })
                }
                className="w-full px-2.5 py-1.5 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 border border-gray-300 rounded-md bg-gray-50 [&::-ms-reveal]:hidden [&::-ms-clear]:hidden"
                minLength={MIN_PASSWORD_LENGTH}
                maxLength={MAX_PASSWORD_LENGTH}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => ({ ...prev, confirmPassword: !prev.confirmPassword }))}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-800"
                aria-label={showPassword.confirmPassword ? "Hide password" : "Show password"}
              >
                {showPassword.confirmPassword ? <LuEye className="w-4 h-4" /> : <LuEyeClosed className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="w-full flex items-center justify-center">
            {loading ? (
              <button
                className="w-[132.4px] h-[40px] bg-[#036494] rounded-full flex items-center justify-center"
                disabled
              >
                <span className="loader"></span>
              </button>
            ) : (
              <button
                type="submit"
                className="w-max px-6 bg-[#036494] text-white py-2 rounded-full hover:bg-[#025177] transition-colors font-light"
              >
                Reset Password
              </button>
            )}
          </div>
          {error && (
            <p className="text-red-500 text-sm text-center mt-2">{error}</p>
          )}
        </div>
      </form>
    </motion.div>
  );
};

export default Step3;
